import { useState } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { formatCurrency } from '../../utils/formatters';
import { useCart, MAX_SAMPLE_ITEMS } from '../../context/CartContext';
import { SAMPLE_ELIGIBLE_UNITS } from '../../utils/constants';
import './ProductCard.css';

const ProductCard = ({ product, compact = false, index = 0 }) => {
  const [selectedVariantId, setSelectedVariantId] = useState(
    product.variants?.[0]?.id || null
  );

  const { items, addItem, updateItem, removeItem, sampleItemCount } = useCart();
  const [sampleAdded, setSampleAdded] = useState(false);

  const selectedVariant = product.variants?.find(v => v.id === selectedVariantId);
  const cartItem = items.find(item => item.productVariantId === selectedVariantId && !item.is_sample);
  const cartQty = cartItem?.quantity || 0;
  const isSampleEligible = selectedVariant && SAMPLE_ELIGIBLE_UNITS.has(selectedVariant.unit);
  const alreadySampled = items.some(item => item.productVariantId === selectedVariantId && item.is_sample);
  const sampleAtMax = sampleItemCount >= MAX_SAMPLE_ITEMS;
  const isOutOfStock = product.inventory_status === 'out_of_stock';
  const isLowInventory = product.inventory_status === 'low_inventory';

  // price_dollars is the API field; fall back to price for compatibility
  const variantPrice = (v) => v?.price_dollars ?? v?.price;

  const handleAdd = () => {
    if (!selectedVariant) return;
    addItem({ productVariantId: selectedVariantId, quantity: 1, product, variant: selectedVariant });
  };

  const handleRequestSample = () => {
    if (!selectedVariant) return;
    addItem({ productVariantId: selectedVariantId, quantity: 1, product, variant: selectedVariant, is_sample: true });
    setSampleAdded(true);
    setTimeout(() => setSampleAdded(false), 2000);
  };

  const handleIncrement = () => updateItem(cartItem.id, cartQty + 1);
  const handleDecrement = () => {
    if (cartQty === 1) removeItem(cartItem.id);
    else updateItem(cartItem.id, cartQty - 1);
  };

  if (!product) return null;

  const initials = product.name
    .split(' ')
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase();

  return (
    <div
      className={`product-card ${compact ? 'product-card--compact' : ''} ${cartQty > 0 ? 'product-card--in-cart' : ''}`}
      style={{ animationDelay: `${index * 0.06}s` }}
    >
      {/* Image / placeholder */}
      <Link to={`/products/${product.id}`} className="product-card__image-wrap" tabIndex={-1} aria-hidden="true">
        {product.image_url ? (
          <img
            src={product.image_url}
            alt={product.name}
            className="product-card__image"
          />
        ) : (
          <div className="product-card__image-placeholder">
            <span className="product-card__image-initials">{initials}</span>
          </div>
        )}
        {cartQty > 0 && !isOutOfStock && (
          <div className="product-card__in-cart-badge">
            {cartQty} in order
          </div>
        )}
        {isOutOfStock && (
          <div className="product-card__inventory-badge product-card__inventory-badge--out">
            Out of Stock
          </div>
        )}
        {isLowInventory && !isOutOfStock && (
          <div className="product-card__inventory-badge product-card__inventory-badge--low">
            Low Inventory
          </div>
        )}
      </Link>

      {/* Content */}
      <div className="product-card__body">
        <Link to={`/products/${product.id}`} className="product-card__title-link">
          <h3 className="product-card__title">{product.name}</h3>
        </Link>

        {!compact && product.description && (
          <p className="product-card__description">{product.description}</p>
        )}

        {/* Growers */}
        {!compact && product.growers && product.growers.length > 0 && (
          <div className="product-card__growers">
            {product.growers.map((g) => (
              <div key={g.id ?? g.farm_name} className="product-card__grower">
                <span className="product-card__grower-name">{g.farm_name}</span>
                {g.region && (
                  <span className="product-card__grower-region">{g.region}</span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Grain variety tags */}
        {!compact && product.grain_varieties && product.grain_varieties.length > 0 && (
          <div className="product-card__grains">
            {product.grain_varieties.map((g) => (
              <span key={g.id} className="product-card__grain-tag">{g.name}</span>
            ))}
          </div>
        )}

        {/* Variant pill selector */}
        {product.variants && product.variants.length > 0 && (
          <div className="product-card__variants">
            <span className="product-card__variants-label">Packaging</span>
            <div className="product-card__pills">
              {product.variants.map((variant) => (
                <button
                  key={variant.id}
                  type="button"
                  className={`product-card__pill ${selectedVariantId === variant.id ? 'product-card__pill--active' : ''}`}
                  onClick={() => setSelectedVariantId(variant.id)}
                >
                  {variant.variant_name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Footer: price + add/stepper */}
        <div className="product-card__footer">
          <div className="product-card__price-wrap">
            {selectedVariant && (
              <>
                <span className="product-card__price">
                  {formatCurrency(variantPrice(selectedVariant))}
                </span>
                {selectedVariant.unit && (
                  <span className="product-card__unit">
                    / {selectedVariant.unit}
                  </span>
                )}
              </>
            )}
          </div>

          {isOutOfStock ? (
            <button
              className="product-card__add-btn product-card__add-btn--unavailable"
              disabled
              aria-label="Out of stock"
            >
              —
            </button>
          ) : cartQty === 0 ? (
            <button
              className="product-card__add-btn"
              onClick={handleAdd}
              disabled={!selectedVariantId}
              aria-label="Add to order"
            >
              +
            </button>
          ) : (
            <div className="product-card__stepper">
              <button
                className="product-card__stepper-btn"
                onClick={handleDecrement}
                aria-label="Decrease quantity"
              >
                −
              </button>
              <span className="product-card__stepper-qty">{cartQty}</span>
              <button
                className="product-card__stepper-btn"
                onClick={handleIncrement}
                aria-label="Increase quantity"
              >
                +
              </button>
            </div>
          )}
        </div>

        {isSampleEligible && !compact && (
          <button
            type="button"
            className={`product-card__sample-btn${alreadySampled || sampleAdded ? ' product-card__sample-btn--added' : ''}${sampleAtMax && !alreadySampled ? ' product-card__sample-btn--disabled' : ''}`}
            onClick={handleRequestSample}
            disabled={sampleAtMax && !alreadySampled}
            title={sampleAtMax && !alreadySampled ? `Max ${MAX_SAMPLE_ITEMS} samples per order` : undefined}
          >
            {alreadySampled || sampleAdded
              ? 'Sample added ✓'
              : sampleAtMax
              ? `Max ${MAX_SAMPLE_ITEMS} samples reached`
              : `Request ${selectedVariant.variant_name} sample`}
          </button>
        )}

        {selectedVariant?.sku && !compact && (
          <div className="product-card__sku">SKU: {selectedVariant.sku}</div>
        )}
      </div>
    </div>
  );
};

ProductCard.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    image_url: PropTypes.string,
    variants: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        variant_name: PropTypes.string.isRequired,
        sku: PropTypes.string,
        price_dollars: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        unit: PropTypes.string,
      })
    ),
  }).isRequired,
  compact: PropTypes.bool,
  index: PropTypes.number,
};

export default ProductCard;
